import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CheckIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, LinkButton } from '../../components/ui/Button';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDateTime, telHref } from '../../utils/format';
import type { Enquiry } from '../../types/admin';
const ease = [0.23, 1, 0.32, 1] as const;
const statusStyles: Record<string, string> = {
  'Ny henvendelse': 'border-[#e6cfc2] bg-accent-soft text-accent-deep',
  Accepteret: 'border-ink/15 bg-ink text-white',
  Behandlet: 'border-line bg-canvas text-ink-soft',
  Afvist: 'border-danger/30 bg-danger/10 text-danger'
};
export function Enquiries() {
  const {
    enquiries,
    dataLoading,
    createCaseFromEnquiry,
    acceptEnquiry,
    deleteEnquiry
  } = useAdminData();
  const navigate = useNavigate();
  const [working, setWorking] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Enquiry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [actionError, setActionError] = useState('');
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-551" data-mp="3kvuhh9yat3s0">
      <p className="text-sm text-ink-muted" data-id="element-552" data-mp="3kwmdumzpz5jc">Fra formularen “Fortæl om din opgave”</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-553" data-mp="3fyuc7199w51y">
        Henvendelser
      </h1>

      {accepted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-554" data-mp="3pv7dak75dl6q">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-555" data-mp="1558b6rcsa6c1">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-556" data-mp="1v3wx1g4nfl8" />
          </span>
          Henvendelsen er accepteret
        </p>}

      {actionError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-557" data-mp="6v8hb2k5ougq">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-558" data-mp="ft2bo7yd1e19" />
          {actionError}
        </p>}

      {deleted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-559" data-mp="3f7e34exb3dhh">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-560" data-mp="3o8c8p1ne7t26">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-561" data-mp="3t2q8dg938mcn" />
          </span>
          Henvendelsen er slettet
        </p>}

      {dataLoading && enquiries.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-562" data-mp="30ihx0net8aji">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-563" data-mp="2szifknawst2s" />
          Henter henvendelser …
        </p>}

      <ul className="mt-7 grid gap-5" data-id="element-564" data-mp="2nzm8ugatunjj">
        {enquiries.map(enquiry => <li key={enquiry.id} className="rounded-card border border-line bg-white p-5 md:p-6" data-id="element-565" data-mp="2jyk97yd009h1">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-566" data-mp="2f0iol8zxc23g">
              <div data-id="element-567" data-mp="2a2zb4bb1vtck">
                <p className="text-xs text-ink-muted" data-id="element-568" data-mp="2a3igytnfera8">{formatDateTime(enquiry.createdAt) || 'Netop nu'}</p>
                <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink" data-id="element-569" data-mp="259wetvew25so">{enquiry.name}</h2>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[enquiry.status] ?? 'border-line bg-canvas text-ink-soft'}`} data-id="element-570" data-mp="1waj654yl9l90">
                {enquiry.status}
              </span>
            </div>

            <dl className="mt-4 grid gap-3 text-[15px] sm:grid-cols-3" data-id="element-571" data-mp="3memd27nzebli">
              <div data-id="element-572" data-mp="3mfe89hdo27b9">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-573" data-mp="3rdpl80z9cxxp">Telefon</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-574" data-mp="3rfiiipnze5t1">
                  <PhoneIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-575" data-mp="3co9toirwkogy" />
                  {enquiry.phone || '—'}
                </dd>
              </div>
              <div data-id="element-576" data-mp="33nv7zib117cx">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-577" data-mp="33nbss9crgx2l">E-mail</dt>
                <dd className="mt-1 flex items-center gap-1.5 break-all text-ink" data-id="element-578" data-mp="37qro92d3grdp">
                  <MailIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-579" data-mp="2tqql1g9cx7v2" />
                  {enquiry.email || '—'}
                </dd>
              </div>
              <div data-id="element-580" data-mp="rykh6ceo9erf">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-581" data-mp="ry2b6o27jm6b">Adresse / område</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-582" data-mp="wxx570ha4rvl">
                  <MapPinIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-583" data-mp="2hfpkba9vezet" />
                  {enquiry.address || '—'}
                </dd>
              </div>
            </dl>

            <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-[15px] leading-relaxed text-ink-soft" data-id="element-584" data-mp="2ces0kt97pxb7">
              {enquiry.description}
            </p>

            {enquiry.images.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-585" data-mp="305xg17b8qnkz">
                {enquiry.images.map(image => <li key={image.path} data-id="element-586" data-mp="2v62pj4vdo00g">
                    <a href={image.url} target="_blank" rel="noreferrer" data-id="element-587" data-mp="2v972aqmws42q">
                      <img src={image.url} alt={image.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-588" data-mp="3a32277536j1s" />
                    </a>
                  </li>)}
              </ul>}

            <div className="mt-5 flex flex-wrap gap-3" data-id="element-589" data-mp="1asu0j2cft0nz">
              {enquiry.status === 'Ny henvendelse' && <Button size="sm" disabled={accepting === enquiry.id} onClick={async () => {
            if (accepting) return;
            setAccepting(enquiry.id);
            setActionError('');
            setDeleted(false);
            const result = await acceptEnquiry(enquiry.id);
            setAccepting(null);
            if (!result.ok) {
              setActionError(result.error ?? 'Henvendelsen kunne ikke accepteres. Prøv igen.');
              return;
            }
            setAccepted(true);
          }} data-id="element-590" data-mp="20hhl9lz7u5lt">
                  {accepting === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-591" data-mp="325tefg7424dy" />
                      Accepterer …
                    </> : 'Accepter henvendelse'}
                </Button>}

              {enquiry.caseId ? <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/sager/${enquiry.caseId}`)} data-id="element-592" data-mp="qglw5yns8wro">
                  Åbn sagen
                </Button> : <Button variant={enquiry.status === 'Ny henvendelse' ? 'secondary' : 'primary'} size="sm" disabled={working === enquiry.id} onClick={async () => {
            setWorking(enquiry.id);
            setActionError('');
            const created = await createCaseFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Sagen kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/sager/${created.id}`);
          }} data-id="element-593" data-mp="gkahx34mtwul">
                  {working === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-594" data-mp="159wa5ou6fe29" />
                      Opretter …
                    </> : 'Opret sag'}
                </Button>}
              {enquiry.phone && <LinkButton href={telHref(enquiry.phone)} variant="secondary" size="sm" data-id="element-595" data-mp="1k76rvfxexg6f">
                  Ring
                </LinkButton>}
              {enquiry.email && <LinkButton href={`mailto:${enquiry.email}`} variant="secondary" size="sm" data-id="element-596" data-mp="1o6fkghgpv80e">
                  Send e-mail
                </LinkButton>}
              <button type="button" onClick={() => {
            setConfirming(enquiry);
            setDeleteError('');
            setDeleted(false);
            setAccepted(false);
            setActionError('');
          }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-597" data-mp="1f6ipjy3qhcww">
                Slet henvendelse
              </button>
            </div>
          </li>)}
      </ul>

      {confirming && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-titel" data-id="element-598" data-mp="38qst8szgcp6a">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-599" data-mp="3igcbpxj96h0m">
            <h2 id="slet-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-600" data-mp="2pyqitzdem54h">
              Er du sikker på, at du vil slette denne henvendelse?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-601" data-mp="nzy7820v54ze">
              Henvendelsen fra {confirming.name} og dens billeder slettes permanent.
            </p>

            {deleteError && <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-602" data-mp="e21u54od7zyn">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-603" data-mp="51vbz5hq2bt5" />
                {deleteError}
              </p>}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-604" data-mp="9u4rxzevb9z">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setDeleteError('');
            const result = await deleteEnquiry(confirming);
            setDeleting(false);
            if (!result.ok) {
              setDeleteError(result.error ?? 'Henvendelsen kunne ikke slettes. Prøv igen.');
              return;
            }
            setConfirming(null);
            setDeleted(true);
          }} data-id="element-605" data-mp="3rirha4ufnbot">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-606" data-mp="26zxf0s7jwr7i" />
                    Sletter …
                  </> : 'Slet henvendelse'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirming(null)} data-id="element-607" data-mp="1ebjdi2h97cr7">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}

      {!dataLoading && enquiries.length === 0 && <p className="mt-8 rounded-card border border-dashed border-line bg-white p-10 text-center text-sm text-ink-muted" data-id="element-608" data-mp="qlywvq8korl3">
          Der er ingen henvendelser endnu.
        </p>}
    </div>;
}